import React from 'react';
import Button from '../../../../components/ui/Button';
import {
  X,
  Package,
  DollarSign,
  Calendar,
  AlertCircle,
  CheckCircle
} from 'lucide-react';

const DetailField = ({ label, value }) => (
  <div>
    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{label}</p>
    <p className="text-sm text-foreground">{value ?? 'N/A'}</p>
  </div>
);

const formatDateTime = (value) => {
  if (!value) return 'N/A';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? 'N/A' : parsed.toLocaleString();
};

const SalesCatalogDetailsModal = ({ isOpen, onClose, catalogItem, isLoading, error }) => {
  if (!isOpen) return null;

  const categoryType = catalogItem?.category_type || 'goods';
  const itemData = categoryType === 'goods' 
    ? (catalogItem?.goods_data || catalogItem?.services_data || {})
    : (catalogItem?.services_data || catalogItem?.goods_data || {});

  const statusBadge = (
    <span
      className={`px-3 py-1 rounded-full text-xs font-semibold ${
        catalogItem?.is_active !== false
          ? 'bg-green-100 text-green-700'
          : 'bg-gray-100 text-gray-700'
      }`}
    >
      {catalogItem?.is_active !== false ? 'Active' : 'Inactive'}
    </span>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-lg border border-gray-200 w-full max-w-5xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-full">
                <Package className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Sales Catalog Details</p>
                <h2 className="text-xl font-semibold text-foreground">
                  {categoryType === 'goods' 
                    ? (itemData?.goods_name || 'Goods Item')
                    : (itemData?.service_name || 'Service Item')}
                </h2>
                <p className="text-sm text-muted-foreground capitalize">
                  {categoryType}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-4 mt-4 text-xs text-muted-foreground">
              <div className="flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" />
                <span>Created {formatDateTime(catalogItem?.created_at)}</span>
              </div>
              <div className="flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" />
                <span>Updated {formatDateTime(catalogItem?.updated_at)}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {statusBadge}
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
              <p className="text-sm text-muted-foreground">Loading catalog item details...</p>
            </div>
          ) : error ? (
            <div className="flex items-center gap-3 p-4 rounded-lg border border-red-200 bg-red-50 text-red-800">
              <AlertCircle className="w-5 h-5" />
              <p className="text-sm">{error}</p>
            </div>
          ) : (
            <>
              <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4 p-4 border border-gray-200 rounded-lg">
                  <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                    <Package className="w-4 h-4" />
                    {categoryType === 'goods' ? 'Goods Information' : 'Service Information'}
                  </h3>
                  {categoryType === 'goods' ? (
                    <>
                      <DetailField label="Goods Name" value={itemData?.goods_name} />
                      <DetailField label="SKU" value={itemData?.sku} />
                      <DetailField label="HSN Code" value={itemData?.hsn_code} />
                      <DetailField label="Units" value={itemData?.units} />
                      <DetailField label="Stock Quantity" value={itemData?.stock_quantity} />
                    </>
                  ) : (
                    <>
                      <DetailField label="Service Name" value={itemData?.service_name} />
                      <DetailField label="SAC Code" value={itemData?.sac_code} />
                    </>
                  )}
                  <DetailField label="Description" value={itemData?.description} />
                </div>

                <div className="space-y-4 p-4 border border-gray-200 rounded-lg">
                  <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                    <DollarSign className="w-4 h-4" />
                    Pricing & Tax
                  </h3>
                  <DetailField label="Price" value={itemData?.price ? `₹${itemData.price}` : 'N/A'} />
                  <DetailField label="GST Treatment" value={itemData?.gst_treatment} />
                  <DetailField label="GST Rate" value={itemData?.gst_rate} />
                  <DetailField label="Net Price" value={itemData?.net_gst?.net_price ? `₹${itemData.net_gst.net_price}` : 'N/A'} />
                  <DetailField label="GST Amount" value={itemData?.net_gst?.gst_amount ? `₹${itemData.net_gst.gst_amount}` : 'N/A'} />
                  <DetailField label="CESS Amount" value={itemData?.cess_amount ? `₹${itemData.cess_amount}` : 'N/A'} />
                  <DetailField label="Non Taxable" value={itemData?.non_taxable ? 'Yes' : 'No'} />
                </div>
              </section>
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 bg-gray-50">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SalesCatalogDetailsModal;

